import { ResourceAccessState } from "@eai-toolkit/client";
import { Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import {
  DetailContainer,
  DetailTitle,
  ErrorPopoverContent,
  StateDescription,
  StateInfoAndActionSection,
  StateSection,
  StatusBox,
  StatusBoxDivider,
  StyledStateIcon,
} from "@components/DetailStatusBox/DetailStatusBoxStyles";

import { ACCESS_REQUEST_EVENTS } from "@helpers/events";

import FieldDisplay from "../../../data/FieldDisplay";
import { StringList } from "../../../data/fieldRenderers/TruncatedStringList";
import type { ResourceFieldValue } from "../../../data/types";
import {
  useCancelAccessRequestMutation,
  useGetAccessRequestByIdQuery,
  useProcessAccessRequestMutation,
} from "../../../store/api/accessRequestsApi";
import { getErrorMessage } from "../../../store/api/baseApi";
import { useGetPermissionsListQuery } from "../../../store/api/permissionsApi";
import { useGetMeQuery } from "../../../store/api/usersApi";
import fieldRenderers from "./data/fieldRenderers";
import {
  accessRequestStateDescription,
  getResourceTypeFromAction,
  getStateBackgroundColor,
  getStateIcon,
} from "./utils";

const Section = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const FieldRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(8),
  marginBottom: theme.spacing(2),
}));

const Field = styled("div")(({ theme }) => ({
  minWidth: theme.spacing(25),
  maxWidth: theme.spacing(50),
  flex: 1,
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  textTransform: "uppercase",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.5px",
}));

const ButtonGroup = styled("div")({
  display: "flex",
  gap: "8px",
});

function AccessRequestDetail() {
  const theme = useTheme();
  const { requestId } = useParams<{ requestId: string }>();

  const {
    data: accessRequest,
    isLoading,
    error,
  } = useGetAccessRequestByIdQuery(
    { requestId: requestId || "" },
    { skip: !requestId },
  );

  const { data: permissionsList } = useGetPermissionsListQuery();
  const { data: me } = useGetMeQuery();

  const [
    cancelAccessRequest,
    { isLoading: isCancelling, error: cancelMutationError },
  ] = useCancelAccessRequestMutation();
  const [
    processAccessRequest,
    { isLoading: isProcessing, error: processMutationError },
  ] = useProcessAccessRequestMutation();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");

  const [actionButtonRef, setActionButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [mutationErrorOpen, setMutationErrorOpen] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState<
    string | null
  >(null);

  const cancelError = getErrorMessage(cancelMutationError);
  const processError = getErrorMessage(processMutationError);

  useEffect(() => {
    if (cancelError || processError) {
      setMutationErrorMessage(cancelError || processError || null);
      setMutationErrorOpen(true);
    }
  }, [cancelError, processError]);

  const handleCloseErrorDialog = () => {
    setMutationErrorOpen(false);
    setMutationErrorMessage(null);
  };

  const isPending = accessRequest?.state === ResourceAccessState.PENDING;
  const isMyRequest = me?.fullName === accessRequest?.requestedBy;
  const isApprover =
    me?.fullName != null &&
    (accessRequest?.recipients ?? []).includes(me.fullName);

  const handleCancel = async () => {
    if (!requestId) return;
    tracker.event(ACCESS_REQUEST_EVENTS.CANCEL_CONFIRMED, requestId);
    await cancelAccessRequest({ requestId });
    setCancelDialogOpen(false);
  };

  const handleReject = async () => {
    if (!requestId) return;
    tracker.event(ACCESS_REQUEST_EVENTS.REJECT_CONFIRMED, requestId);
    await processAccessRequest({
      requestId,
      state: ResourceAccessState.REJECTED,
    });
    setRejectDialogOpen(false);
  };

  const handleApprove = async () => {
    if (!requestId) return;
    tracker.event(ACCESS_REQUEST_EVENTS.APPROVE_CONFIRMED, requestId);
    await processAccessRequest({
      requestId,
      state: ResourceAccessState.APPROVED,
      processingRemarks: approvalRemarks || undefined,
    });
    setApproveDialogOpen(false);
    setApprovalRemarks("");
  };

  if (isLoading) {
    return (
      <DetailContainer>
        <CircularProgress />
      </DetailContainer>
    );
  }

  if (error) {
    return (
      <DetailContainer>
        <Alert severity="error">
          Failed to load access request details. Please try again.
        </Alert>
      </DetailContainer>
    );
  }

  if (!accessRequest) {
    return (
      <DetailContainer>
        <Alert severity="info">Access request not found.</Alert>
      </DetailContainer>
    );
  }

  const StateIcon = getStateIcon(accessRequest.state);

  return (
    <DetailContainer>
      <DetailTitle variant="h2">Access request detail</DetailTitle>
      <StatusBox>
        <StateSection>
          <StyledStateIcon
            aria-hidden="true"
            style={{
              color: getStateBackgroundColor(
                accessRequest.state ?? "",
                theme.palette,
              ),
            }}
          >
            <StateIcon color="inherit" fontSize="inherit" />
          </StyledStateIcon>
          <StateDescription>
            <Typography variant="h3">
              {accessRequestStateDescription(accessRequest.state)}
            </Typography>
            <FieldDisplay
              fieldValue={accessRequest.state}
              dataType="State"
              fieldRenderers={fieldRenderers}
            />
          </StateDescription>
        </StateSection>
        <StatusBoxDivider />
        <StateInfoAndActionSection>
          {/* This is where we can add additional state info, but right now we don't have any so the div is just a placeholder */}
          <div />

          {isPending && isMyRequest && (
            <Button
              color="inherit"
              disabled={isCancelling}
              onClick={() => {
                tracker.event(
                  ACCESS_REQUEST_EVENTS.CANCEL_BUTTON_CLICKED,
                  requestId ?? "",
                );
                setCancelDialogOpen(true);
              }}
              ref={(ref) => setActionButtonRef(ref)}
            >
              Cancel
            </Button>
          )}
          {isPending && !isMyRequest && isApprover && (
            <ButtonGroup>
              <Button
                color="error"
                disabled={isProcessing}
                onClick={() => {
                  tracker.event(
                    ACCESS_REQUEST_EVENTS.REJECT_BUTTON_CLICKED,
                    requestId ?? "",
                  );
                  setRejectDialogOpen(true);
                }}
                ref={(ref) => setActionButtonRef(ref)}
              >
                Deny
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={isProcessing}
                onClick={() => {
                  tracker.event(
                    ACCESS_REQUEST_EVENTS.APPROVE_BUTTON_CLICKED,
                    requestId ?? "",
                  );
                  setApproveDialogOpen(true);
                }}
                ref={(ref) => setActionButtonRef(ref)}
              >
                Approve
              </Button>
            </ButtonGroup>
          )}
        </StateInfoAndActionSection>
      </StatusBox>
      <Popover
        open={mutationErrorOpen}
        anchorEl={actionButtonRef}
        onClose={handleCloseErrorDialog}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
      >
        <ErrorPopoverContent role="alert">
          <Typography variant="subtitle2" className="error-label">
            Error:
          </Typography>
          <Typography variant="body2" className="error-message">
            {mutationErrorMessage &&
              `"${mutationErrorMessage.slice(0, 250)}${
                mutationErrorMessage.length > 250 ? "..." : ""
              }"`}
          </Typography>
          <IconButton
            size="small"
            aria-label="close"
            className="close-button"
            onClick={handleCloseErrorDialog}
          >
            <Close fontSize="small" />
          </IconButton>
        </ErrorPopoverContent>
      </Popover>
      <Section>
        <SectionHeader variant="h3">Access details</SectionHeader>
        <Divider sx={{ mb: 2 }} />
        <FieldRow>
          <Field>
            <FieldLabel>ID</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.requestId}
              dataType="ID"
              fieldRenderers={fieldRenderers}
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field>
            <FieldLabel>Resource</FieldLabel>
            <FieldDisplay
              fieldValue={
                {
                  name: accessRequest.resource,
                  // There is a accessRequest.resourceType field, but it seems to be empty so I did this workaround instead
                  resourceType:
                    accessRequest.resourceType ??
                    getResourceTypeFromAction(
                      accessRequest.action,
                      permissionsList?.items ?? [],
                    ),
                  // Add the below line back in if we want to display the cascades field in the resource field
                  // cascades: accessRequest.cascades,
                } as ResourceFieldValue
              }
              dataType="Resource"
              fieldRenderers={fieldRenderers}
            />
          </Field>
          <Field>
            <FieldLabel>Action</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.action}
              dataType="Text"
              fieldRenderers={fieldRenderers}
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field>
            <FieldLabel>Target</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.target}
              dataType="Text"
              fieldRenderers={fieldRenderers}
            />
          </Field>
          <Field>
            <FieldLabel>Scope</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.scope}
              dataType="Text"
              fieldRenderers={fieldRenderers}
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field>
            <FieldLabel>Cascades</FieldLabel>
            <Typography>{accessRequest.cascades ? "Yes" : "No"}</Typography>
          </Field>
          <Field>
            <FieldLabel>Shareable</FieldLabel>
            <Typography>{accessRequest.shareable ? "Yes" : "No"}</Typography>
          </Field>
        </FieldRow>
      </Section>

      <Section>
        <SectionHeader variant="h3">Timeline</SectionHeader>
        <Divider sx={{ mb: 2 }} />
        <FieldRow>
          <Field>
            <FieldLabel>Created</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.createdAt}
              dataType="Date"
              fieldRenderers={fieldRenderers}
            />
          </Field>
          <Field>
            <FieldLabel>Request expires</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.expiresAt}
              dataType="Date"
              fieldRenderers={fieldRenderers}
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field>
            <FieldLabel>Access expires</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.expiration}
              dataType="Date"
              fieldRenderers={fieldRenderers}
            />
          </Field>
          <Field>
            <FieldLabel>Processed</FieldLabel>
            <FieldDisplay
              fieldValue={accessRequest.processedAt}
              dataType="Date"
              fieldRenderers={fieldRenderers}
            />
          </Field>
        </FieldRow>
      </Section>

      <Section>
        <SectionHeader variant="h3">People</SectionHeader>
        <Divider sx={{ mb: 2 }} />
        <FieldRow>
          <Field>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              <div>
                <FieldLabel>Requested by</FieldLabel>
                <FieldDisplay
                  fieldValue={accessRequest.requestedBy}
                  dataType="Text"
                  fieldRenderers={fieldRenderers}
                />
              </div>
              <div>
                <FieldLabel>Processed by</FieldLabel>
                <FieldDisplay
                  fieldValue={accessRequest.processedBy}
                  dataType="Text"
                  fieldRenderers={fieldRenderers}
                />
              </div>
            </div>
          </Field>
          <Field>
            <FieldLabel>Approvers</FieldLabel>
            {accessRequest.recipients && accessRequest.recipients.length > 0 ? (
              StringList(accessRequest.recipients).node
            ) : (
              <Typography>-</Typography>
            )}
          </Field>
        </FieldRow>
      </Section>

      <Section>
        <SectionHeader variant="h3">Request reason</SectionHeader>
        <Divider sx={{ mb: 2 }} />
        <Typography>{accessRequest.requestReason || "— (none)"}</Typography>
      </Section>

      {accessRequest.processingRemarks && (
        <Section>
          <SectionHeader variant="h3">Processing remarks</SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Typography>{accessRequest.processingRemarks}</Typography>
        </Section>
      )}

      <ConfirmationDialog
        open={cancelDialogOpen}
        title="Cancel request"
        message="Are you sure you want to cancel this access request?"
        confirmText="Yes, cancel"
        confirmColor="error"
        cancelText="No"
        onConfirm={handleCancel}
        onCancel={() => setCancelDialogOpen(false)}
        isLoading={isCancelling}
      />

      <ConfirmationDialog
        open={rejectDialogOpen}
        title="Reject request"
        message="Are you sure you want to reject this access request?"
        confirmText="Yes, reject"
        confirmColor="error"
        cancelText="No"
        onConfirm={handleReject}
        onCancel={() => setRejectDialogOpen(false)}
        isLoading={isProcessing}
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        title="Approve request"
        message="Add optional remarks for this approval."
        confirmText="Approve"
        confirmColor="primary"
        onConfirm={handleApprove}
        onCancel={() => setApproveDialogOpen(false)}
        isLoading={isProcessing}
        maxWidth="sm"
        inputLabel="Remarks (optional)"
        inputValue={approvalRemarks}
        onInputChange={setApprovalRemarks}
      />
    </DetailContainer>
  );
}

export default AccessRequestDetail;
